import {
  FC,
  useState,
  useEffect,
  createContext,
  useContext,
  useRef,
  Dispatch,
  SetStateAction,
} from 'react';
import {LayoutSplashScreen} from '../../../../_metronic/layout/core';
import {AuthModel, UserModel} from './_models';
import * as authHelper from './AuthHelpers';
import {getUserByToken, logout as inactivcateToken} from './_requests';
import {WithChildren} from '../../../../_metronic/helpers';
import {DEFAULT_ROLE} from '../../../../app-locator';
import {useBranding} from '../../app/core/BrandingProvider';
import {getOrganizationById} from '../../admin/api/organizations-api';
import {useNavigate} from 'react-router-dom';

type AuthContextProps = {
  auth: AuthModel | undefined;
  saveAuth: (auth: AuthModel | undefined) => void;
  currentUser: UserModel | undefined;
  setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>;
  logout: () => void;
};

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  saveAuth: () => {},
  currentUser: undefined,
  setCurrentUser: () => {},
  logout: () => {},
};

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState);

const useAuth = () => {
  return useContext(AuthContext);
};

const AuthProvider: FC<WithChildren> = ({children}) => {
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth());
  const [currentUser, setCurrentUser] = useState<UserModel | undefined>();
  const {setOrganization} = useBranding();

  const saveAuth = (auth: AuthModel | undefined) => {
    setAuth(auth);
    if (auth) {
      authHelper.setAuth(auth);
    } else {
      authHelper.removeAuth();
    }
  };

  const logout = () => {
    if (auth) inactivcateToken(auth.api_token);
    setOrganization(undefined);
    saveAuth(undefined);
    setCurrentUser(undefined);
  };

  return (
    <AuthContext.Provider value={{auth, saveAuth, currentUser, setCurrentUser, logout}}>
      {children}
    </AuthContext.Provider>
  );
};

const AuthInit: FC<WithChildren> = ({children}) => {
  const {auth, logout, setCurrentUser} = useAuth();
  const didRequest = useRef(false);
  const [showSplashScreen, setShowSplashScreen] = useState(true);
  const {organization, setOrganization} = useBranding();

  // We should request user by authToken (IN OUR EXAMPLE IT'S API_TOKEN) before rendering the application
  useEffect(() => {
    const requestUser = async (apiToken: string) => {
      try {
        if (!didRequest.current) {
          const {data: user} = await getUserByToken(apiToken);
          if (user) {
            // if (user.org_id) {
            const org = await getOrganizationById(user.org_id || 0);
            setOrganization(org);
            // }

            setCurrentUser({
              ...user,
              role_id: user.role_id ?? DEFAULT_ROLE,
              is_super_admin: !!user.is_super_admin,
              is_org_admin: !!user.is_org_admin,
            });
          }
        }
      } catch (error) {
        console.error(error);
        if (!didRequest.current) {
          logout();
        }
      } finally {
        setShowSplashScreen(false);
      }

      return () => (didRequest.current = true);
    };

    if (auth && auth.api_token) {
      // alert('getting user');
      requestUser(auth.api_token);
    } else {
      logout();
      setShowSplashScreen(false);
    }
    // eslint-disable-next-line
  }, []);

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>;
};

export {AuthProvider, AuthInit, useAuth};
